import React from 'react';
import { User, Instagram, Facebook, Globe } from 'lucide-react';

interface MobileCardPreviewProps {
  content?: string;
  imageUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  contentType: 'text' | 'image' | 'video' | 'audio';
  showCreatorInfo?: boolean;
  creatorName?: string;
  socialPlatform?: string;
  socialUrl?: string;
}

const MobileCardPreview: React.FC<MobileCardPreviewProps> = ({
  content = '',
  imageUrl,
  videoUrl,
  audioUrl,
  contentType,
  showCreatorInfo = false,
  creatorName = 'Creator Name',
  socialPlatform,
  socialUrl,
}) => {

  const getSocialIcon = () => {
    switch (socialPlatform) {
      case 'instagram':
        return <Instagram className="w-4 h-4" />;
      case 'facebook':
        return <Facebook className="w-4 h-4" />;
      case 'tiktok':
        return <span className="text-xs font-bold">TT</span>;
      case 'web':
        return <Globe className="w-4 h-4" />;
      default:
        return <User className="w-4 h-4" />;
    }
  };

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden border-4 border-gray-800">
        {/* Phone Header Bar */}
        <div className="bg-gray-800 h-8 flex items-center justify-between px-4">
          <div className="flex items-center space-x-1">
            <div className="w-1 h-1 rounded-full bg-gray-400"></div>
            <div className="w-1 h-1 rounded-full bg-gray-400"></div>
            <div className="w-1 h-1 rounded-full bg-gray-400"></div>
          </div>
          <div className="flex items-center space-x-1">
            <div className="text-white text-xs">--:--</div>
          </div>
        </div>

        {/* Card Content */}
        <div className="bg-creator-bg dark:bg-gray-900 min-h-[400px] max-h-[500px] overflow-y-auto p-4 space-y-3">
          {/* Media Preview */}
          {contentType === 'image' && imageUrl && (
            <div className="w-full rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-700">
              <img
                src={imageUrl}
                alt="Card preview"
                className="w-full h-48 object-cover"
              />
            </div>
          )}

          {contentType === 'video' && videoUrl && (
            <div className="w-full rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-700 aspect-video">
              <div className="w-full h-full flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                    <span className="text-2xl">▶</span>
                  </div>
                  <p className="text-xs">Video Preview</p>
                </div>
              </div>
            </div>
          )}

          {contentType === 'audio' && audioUrl && (
            <div className="w-full rounded-lg bg-gray-200 dark:bg-gray-700 p-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-creator-accent flex items-center justify-center text-white">
                  <span className="text-lg">🎵</span>
                </div>
                <div className="flex-1">
                  <div className="h-2 bg-gray-300 dark:bg-gray-600 rounded-full">
                    <div className="h-full w-1/3 bg-creator-accent rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Text Content */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm">
            {content ? (
              <div 
                className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            ) : (
              <p className="text-sm text-gray-400 italic">
                Your card content will appear here...
              </p>
            )}
          </div>

          {/* Creator Info */}
          {showCreatorInfo && (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm border-t-2 border-creator-accent">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-full bg-creator-accent flex items-center justify-center text-white text-xs font-medium">
                    {creatorName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-200">
                      {creatorName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Content Creator</p>
                  </div>
                </div>

                {socialPlatform && socialUrl && (
                  <div
                    className="p-2 bg-creator-bg dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-creator-accent hover:text-white transition-all"
                    title={`View on ${socialPlatform}`}
                  >
                    {getSocialIcon()}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Phone Bottom Bar */}
        <div className="bg-gray-800 h-8 flex items-center justify-center">
          <div className="w-1/3 h-1 bg-gray-600 rounded-full"></div>
        </div>
      </div>

      {/* Preview Label */}
      <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-2">
        Mobile App Preview
      </p>
    </div>
  );
};

export default MobileCardPreview;
